import { startsWith } from 'lodash';
import envVar, { getSpecificEnv } from '@availity/env-var';

const specificEnv = getSpecificEnv();

// the current on-prem url prefix for FR UI. AWS url prefix commented out for now until AWS nav is ready.
const avUrlPrefix = specificEnv === 'prod' ? 'apps' : `${specificEnv}-apps`;
const essentialsUrlPrefix = specificEnv === 'prod' ? 'essentials' : `${specificEnv}-essentials`;

// the url prefix for FR
const frEnvPrefix = envVar({
  prod: 'login-prod',
  qa: 'login-qua',
  qap: 'login-qap',
  local: 'login-tst',
});

// the acceptable URL prefixes for the goto url
const acceptableGotoUrlPrefixes =
  specificEnv === 'prod'
    ? ['https://apps.availity', 'https://essentials.availity', 'https://login-prod.availity']
    : [
        `https://${specificEnv}-apps.availity`,
        `https://${specificEnv}-essentials.availity`,
        `https://${frEnvPrefix}.availity`,
      ];

export default function handleSuccessNavigation(gotoValue, shouldGoToInvitationsUI = false, navVersion = '1') {
  const { host = '', href: currentHref } = window.location;
  // const { origin: topOrigin = '' } = window.top.location;
  const lowercaseHost = host?.toLowerCase();

  // ensure correct url prefix is used - 'apps vs essentials' dot availity dot com
  // NOTE (01/24/25): until we figure out how to handle rendering on-prem (-apps) content from the AWS (-essentials) nav domain without needing a tyk url, we need to force the onprem nav.
  // const urlPrefixToUse = lowercaseHost.indexOf('essentials') >= 0 ? essentialsUrlPrefix : avUrlPrefix;

  const windowHasNav = currentHref.indexOf('/public/apps/home') >= 0 || currentHref.indexOf('/web/spc/navigation') >= 0; // determine if location has the availity nav; check for 'loadapp' in the url
  const hasDotNet = lowercaseHost.indexOf('.net') >= 0; // determine if the portal loaded in .net or .com
  const domainSuffix = hasDotNet ? '.net' : '.com'; // set the domain suffix
  const baseAvUrl = `https://${avUrlPrefix}.availity${domainSuffix}`; // set the base av url
  const essentialsBaseUrl = `https://${essentialsUrlPrefix}.availity${domainSuffix}`; // set the base essentials url

  // first need to determine if the user should go to the invitations UI. prior to calling the handleSuccessNavigation function, it should be determined if the user has open invitations as well as if the user is in the login flow. otherwise, assume the user doesn't need to go to the invitations UI.
  if (shouldGoToInvitationsUI) {
    const invitationsUrl = `${essentialsBaseUrl}/static/web-protected/onb/epum/dashboard-settings-ui/?module=invite-list&from=login`;

    window.top.location.href = invitationsUrl;
  } else {
    // define nav and dashboard paths
    const angularNavPath = '/public/apps/home/#!/';
    const cloudNavPath = '/static/web/onb/onboarding-ui-apps/navigation/#/';

    const onpremDashboardUrl = `${baseAvUrl}/public/apps/dashboard`;
    const cloudDashboardUrl = `${essentialsBaseUrl}/static/web/onb/onboarding-ui-apps/dashboard-ui/#/`;

    let navUrlToUse;
    let dashboardUrlToUse;
    if (navVersion === '3') {
      navUrlToUse = `${essentialsBaseUrl}${cloudNavPath}`;
      dashboardUrlToUse = cloudDashboardUrl;
    } else {
      navUrlToUse = `${baseAvUrl}${angularNavPath}`;
      dashboardUrlToUse = onpremDashboardUrl;
    }

    // default goto should be the main portal with the nav.
    // if the current location has the nav, then default to the home page, else default to the dashboard
    const defaultGoto = windowHasNav ? dashboardUrlToUse : navUrlToUse;

    // determine if the goto value is safe by comparing with the acceptable url prefixes, else use the default goto value
    let gotoValueToUse = defaultGoto;

    if (gotoValue) {
      for (const acceptablePrefix of acceptableGotoUrlPrefixes) {
        if (startsWith(gotoValue, acceptablePrefix)) {
          gotoValueToUse = gotoValue;
        }
      }
    }

    // special handling for agent responses - need to inject the nav into the url
    if (gotoValueToUse.indexOf('/agent') >= 0) {
      const agentUrl = gotoValueToUse.split('/agent');
      const encodedAgentUrl = encodeURIComponent(`/agent${agentUrl[1]}`);
      gotoValueToUse = `${defaultGoto}loadApp?appUrl=${encodedAgentUrl}`;
    }

    const gotoHasNav =
      gotoValueToUse.indexOf('/public/apps/home') >= 0 || gotoValueToUse.indexOf('/web/spc/navigation') >= 0;

    // if the current goto has a nav - set it at the top window, else set it at the current window
    // goal is to avoid rendering double nav bars
    if (gotoHasNav) {
      window.top.location.href = gotoValueToUse;
    } else {
      window.location.href = gotoValueToUse;
    }
  }
}
